/**
 * Domain validation and calculation utilities for Kurslederopplæring (Instructor Training).
 * Pure functions with zero external dependencies to maintain CRAP <= 8 and high testability.
 */

export const TRAINING_FORMATS = {
    DIGITAL: "digital",
    FYSISK: "fysisk",
    HYBRID: "hybrid",
};

export const TRAINING_STATUSES = {
    OPEN: "open",
    FEW_SEATS: "few_seats",
    FULL: "full",
    COMPLETED: "completed",
};

export const TRAINING_STATUS_CONFIG = {
    [TRAINING_STATUSES.OPEN]: {
        label: "Ledige plasser",
        color: "success",
        isBookable: true,
    },
    [TRAINING_STATUSES.FEW_SEATS]: {
        label: "Få plasser igjen",
        color: "warning",
        isBookable: true,
    },
    [TRAINING_STATUSES.FULL]: {
        label: "Fulltegnet",
        color: "error",
        isBookable: false,
    },
    [TRAINING_STATUSES.COMPLETED]: {
        label: "Gjennomført",
        color: "default",
        isBookable: false,
    },
};

export const getComputedTrainingStatus = (training, now = new Date()) => {
    if (!training) {
        return { label: "Ukjent", color: "default", isBookable: false, isClosed: true };
    }
    if (training.status === TRAINING_STATUSES.COMPLETED) {
        return { label: "Gjennomført", color: "default", isBookable: false, isClosed: true };
    }
    if (training.registrationDeadline && isDeadlinePassed(training.registrationDeadline, now)) {
        return { label: "Påmelding stengt", color: "default", isBookable: false, isClosed: true, reason: "deadline_passed" };
    }
    if (training.status === TRAINING_STATUSES.FULL) {
        return { label: "Fulltegnet", color: "error", isBookable: false, isClosed: true };
    }
    if (training.status === TRAINING_STATUSES.FEW_SEATS) {
        return { label: "Få plasser igjen", color: "warning", isBookable: true, isClosed: false };
    }
    return { label: "Ledige plasser", color: "success", isBookable: true, isClosed: false };
};

export const isValidTrainingStatus = (status) => {
    return Object.values(TRAINING_STATUSES).includes(status);
};

export const isValidTrainingFormat = (format) => {
    return Object.values(TRAINING_FORMATS).includes(format);
};

export const isValidEmail = (email) => {
    if (!email || typeof email !== "string") return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

export const isValidDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return false;
    const match = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    if (!match) return false;
    const d = new Date(dateStr);
    return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === dateStr;
};

export const isDeadlinePassed = (deadline, now = new Date()) => {
    if (!deadline || !isValidDateString(deadline)) return false;
    const deadlineDate = new Date(`${deadline}T23:59:59`);
    const referenceDate = now instanceof Date ? now : new Date(now);
    return referenceDate.getTime() > deadlineDate.getTime();
};

export const getDaysUntilDeadline = (deadline, now = new Date()) => {
    if (!deadline || !isValidDateString(deadline)) return null;
    const deadlineDate = new Date(`${deadline}T23:59:59`);
    const referenceDate = now instanceof Date ? now : new Date(now);
    const diffMs = deadlineDate.getTime() - referenceDate.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export const validateTrainingSession = (session, index = 0) => {
    const errors = {};
    if (!session || typeof session !== "object") {
        return { session: "Ugyldig samlingsobjekt" };
    }
    const sessionNumber = Number(session.sessionNumber || index + 1);
    if (!Number.isInteger(sessionNumber) || sessionNumber < 1) {
        errors.sessionNumber = "Samlingsnummer må være et positivt heltall";
    }
    if (!session.dates || typeof session.dates !== "string" || session.dates.trim().length < 2) {
        errors.dates = "Datoer for samlingen må oppgis (f.eks. '7. - 9. september')";
    }
    return errors;
};

export const validateInstructorTraining = (data) => {
    const errors = {};
    if (!data || typeof data !== "object") {
        return { general: "Ugyldig kurslederopplæringsobjekt" };
    }

    // Title
    const title = typeof data.title === "string" ? data.title.trim() : "";
    if (!title || title.length < 3) {
        errors.title = "Tittel må være minst 3 tegn";
    } else if (title.length > 120) {
        errors.title = "Tittel kan ikke overskride 120 tegn";
    }

    // Format
    if (!isValidTrainingFormat(data.format)) {
        errors.format = "Format må være 'digital', 'fysisk' eller 'hybrid'";
    }

    // Status
    if (!isValidTrainingStatus(data.status)) {
        errors.status = "Ugyldig status";
    }

    // Instructor name
    const instructorName = typeof data.instructorName === "string" ? data.instructorName.trim() : "";
    if (!instructorName || instructorName.length < 2) {
        errors.instructorName = "Kursholder må oppgis (f.eks. 'Psykologspesialist Serina Vorland')";
    }

    // Registration Deadline
    if (data.registrationDeadline) {
        if (!isValidDateString(data.registrationDeadline)) {
            errors.registrationDeadline = "Påmeldingsfrist må være på formatet ÅÅÅÅ-MM-DD";
        }
    }

    // Registration Email
    if (data.registrationEmail && !isValidEmail(data.registrationEmail)) {
        errors.registrationEmail = "Ugyldig e-postadresse for påmelding";
    }

    // Sessions
    if (Array.isArray(data.sessions)) {
        data.sessions.forEach((session, i) => {
            const sessionErrors = validateTrainingSession(session, i);
            if (Object.keys(sessionErrors).length > 0) {
                if (!errors.sessions) errors.sessions = {};
                errors.sessions[i] = sessionErrors;
            }
        });
    }

    return errors;
};
